import { useEffect, useState } from "react";
import { Clock, Users } from "lucide-react";
import api from "../lib/api";
import InfoTip from "./InfoTip";

/**
 * Lead Response Cards · Two-KPI split (spec IT · Miosotis Peña · Julio 2026)
 *
 *   ▸ Primer Tiempo de Respuesta (FTR) — BDC contact center intake
 *     (Nissan / Chevrolet / Suzuki). Ranking = miembros del BDC.
 *   ▸ Gestión de Leads — piso / floor sales reps
 *     (Nissan / Chevrolet / Suzuki / Cadillac / Infiniti).
 *     Ranking = ejecutivos de piso.
 *
 * Layout (per stakeholder feedback):
 *   - Two BIG numbers on top: % Contactado + Promedio de tiempo.
 *   - SLA distribution as an actionable donut (Cumple / Mismo día /
 *     Siguiente día / Tardío) — decision-ready segmentation instead of
 *     a 5-bar histogram that hides the SLA-hit signal.
 *   - Ranking of top 5 owners preserved for accountability.
 *
 * Data source: `GET /api/leads/response-time` → `{ftr, gestion, config}`.
 */
const fmtHours = (h) => {
  if (h == null) return "—";
  if (h < 1)  return `${Math.round(h * 60)}min`;
  if (h < 24) return `${h.toFixed(1)}h`;
  return `${(h / 24).toFixed(1)}d`;
};
const fmtNum = (n) => new Intl.NumberFormat("es-DO").format(Math.round(n || 0));

// SLA colors — semantic (green/yellow/orange/red) aligned with the 4-bucket
// partition returned by the backend (Cumple SLA / Mismo día / Siguiente día / Tardío).
const SLA_COLORS = ["#16a34a", "#eab308", "#f97316", "#dc2626"];

// Compact SVG donut renderer with an HTML overlay for the center label —
// SVG text renders inconsistently across fonts/browsers at small sizes, so
// the center reading uses a positioned div for reliable typography.
function SlaDonut({ buckets, size = 148, thickness = 22 }) {
  const total = buckets.reduce((s, b) => s + (b.n || 0), 0);
  if (!total) {
    return (
      <div className="flex items-center justify-center text-[11px] text-gray-400"
        style={{ height: size, width: size }}>
        Sin datos
      </div>
    );
  }
  const cx = size / 2, cy = size / 2, r = (size - thickness) / 2;
  let acc = 0;
  return (
    <div style={{ position: "relative", width: size, height: size }}>
      <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} data-testid="sla-donut">
        {buckets.map((b, i) => {
          const val = b.n || 0;
          if (val === 0) return null;
          const startAng = (acc / total) * 2 * Math.PI - Math.PI / 2;
          acc += val;
          const endAng = (acc / total) * 2 * Math.PI - Math.PI / 2;
          const large = endAng - startAng > Math.PI ? 1 : 0;
          const x1 = cx + r * Math.cos(startAng), y1 = cy + r * Math.sin(startAng);
          const x2 = cx + r * Math.cos(endAng),   y2 = cy + r * Math.sin(endAng);
          return (
            <path key={b.label}
              d={`M ${x1} ${y1} A ${r} ${r} 0 ${large} 1 ${x2} ${y2}`}
              fill="none"
              stroke={SLA_COLORS[i]}
              strokeWidth={thickness}
              strokeLinecap="butt"
            />
          );
        })}
      </svg>
      {/* Center label — HTML overlay for consistent typography */}
      <div style={{
        position: "absolute", inset: 0,
        display: "flex", flexDirection: "column",
        alignItems: "center", justifyContent: "center",
        pointerEvents: "none",
      }}>
        <div className="tabular-nums font-extrabold"
          style={{ fontFamily: "Manrope, sans-serif", fontSize: 24,
                   color: SLA_COLORS[0], lineHeight: 1, letterSpacing: "-0.02em" }}>
          {(buckets[0]?.pct || 0).toFixed(0)}%
        </div>
        <div className="text-[9px] font-bold uppercase mt-1"
          style={{ color: "#64748b", letterSpacing: "0.05em" }}>
          Cumple SLA
        </div>
      </div>
    </div>
  );
}

function KpiPanel({ kpi, title, subtitle, tooltip, brandScope, accent, rankingLabel, testid }) {
  if (!kpi) return null;
  const avg    = kpi.response_time_hours?.avg    || 0;
  const median = kpi.response_time_hours?.median || 0;
  const rateColor = kpi.attended_pct >= 60 ? "#16a34a"
                  : kpi.attended_pct >= 40 ? "#f97316" : "#dc2626";
  const avgColor  = avg < 2  ? "#16a34a"
                  : avg < 8  ? "#eab308"
                  : avg < 24 ? "#f97316" : "#dc2626";

  return (
    <section className="surface" data-testid={testid} style={{ padding: 0, overflow: "hidden" }}>
      {/* Header */}
      <div style={{ padding: "16px 20px 12px", borderBottom: "1px solid #f1f5f9" }}>
        <div className="flex items-center gap-2">
          <div className="kpi-icon-clean" style={{
            width: 32, height: 32, color: accent,
            boxShadow: `inset 0 0 0 1px ${accent}33`,
          }}>
            <Clock size={16}/>
          </div>
          <div className="flex-1 min-w-0">
            <h3 className="font-display font-extrabold text-base flex items-center gap-1"
              style={{ letterSpacing: "-0.01em" }}>
              {title}
              <InfoTip>{tooltip}</InfoTip>
            </h3>
            <p className="text-[11px] text-gray-500 font-semibold">
              {subtitle} · <span style={{ color: accent }}>{brandScope}</span>
            </p>
          </div>
        </div>
      </div>

      {/* Body: two headline numbers + donut with legend */}
      <div className="grid gap-4"
        style={{ padding: "18px 20px", gridTemplateColumns: "1fr 168px" }}>
        {/* Left column — Contact % + Avg */}
        <div className="space-y-4">
          <div>
            <div className="text-[10px] font-extrabold uppercase tracking-wider text-gray-500">
              % Contactado del total
            </div>
            <div className="flex items-baseline gap-2 mt-1"
              style={{ fontFamily: "Manrope, sans-serif" }}>
              <span className="font-extrabold tabular-nums"
                style={{ fontSize: 36, color: rateColor, letterSpacing: "-0.03em", lineHeight: 1 }}>
                {kpi.attended_pct}%
              </span>
              <span className="text-[11px] text-gray-500 font-semibold">
                {fmtNum(kpi.attended)} / {fmtNum(kpi.total_leads)}
              </span>
            </div>
          </div>
          <div>
            <div className="text-[10px] font-extrabold uppercase tracking-wider text-gray-500">
              Tiempo promedio de contacto
            </div>
            <div className="flex items-baseline gap-2 mt-1"
              style={{ fontFamily: "Manrope, sans-serif" }}>
              <span className="font-extrabold tabular-nums"
                style={{ fontSize: 30, color: avgColor, letterSpacing: "-0.03em", lineHeight: 1 }}>
                {fmtHours(avg)}
              </span>
              <span className="text-[11px] text-gray-500 font-semibold">
                Mediana: {fmtHours(median)}
              </span>
            </div>
          </div>
        </div>

        {/* Right column — donut */}
        <div className="flex items-center justify-center">
          <SlaDonut buckets={kpi.sla_buckets || []}/>
        </div>
      </div>

      {/* Legend rows (below donut, full width) */}
      <div style={{ padding: "0 20px 12px" }}>
        <div className="grid gap-1.5" style={{ gridTemplateColumns: "repeat(2, 1fr)" }}>
          {kpi.sla_buckets?.map((b, i) => (
            <div key={b.label} className="flex items-center gap-2 text-[11px]"
              data-testid={`${testid}-sla-${i}`}>
              <span style={{
                width: 10, height: 10, borderRadius: 2,
                background: SLA_COLORS[i], flexShrink: 0,
              }}/>
              <span className="font-semibold text-gray-700 truncate">{b.label}</span>
              <span className="ml-auto tabular-nums font-bold" style={{ color: SLA_COLORS[i] }}>
                {b.pct}%
              </span>
              <span className="text-[10px] text-gray-400 tabular-nums w-10 text-right">
                {fmtNum(b.n)}
              </span>
            </div>
          ))}
        </div>
      </div>

      {/* Ranking */}
      {kpi.ranking?.length > 0 && (
        <div style={{ padding: "12px 20px 20px", borderTop: "1px solid #f1f5f9" }}>
          <div className="text-[10px] font-extrabold uppercase tracking-wider text-gray-500 mb-2 flex items-center gap-1">
            <Users size={11}/> {rankingLabel}
          </div>
          <div className="space-y-1.5" data-testid={`${testid}-ranking`}>
            {kpi.ranking.slice(0, 5).map((b, i) => {
              const c = b.median_hours < 2 ? "#16a34a"
                      : b.median_hours < 8 ? "#eab308"
                      : b.median_hours < 24 ? "#f97316" : "#dc2626";
              return (
                <div key={b.owner}
                  className="grid items-center gap-2 text-xs"
                  style={{ gridTemplateColumns: "20px 1fr 70px 60px" }}
                  data-testid={`${testid}-row-${i}`}>
                  <span className="text-[10px] font-extrabold text-gray-400 tabular-nums">{i + 1}</span>
                  <span className="font-bold text-gray-900 truncate" title={b.owner}>{b.owner}</span>
                  <span className="text-right tabular-nums text-gray-600 font-semibold">
                    {fmtNum(b.attended)} leads
                  </span>
                  <span className="text-right tabular-nums font-extrabold" style={{ color: c }}>
                    {fmtHours(b.median_hours)}
                  </span>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </section>
  );
}

export default function ResponseTimeCard({ filters = {} }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let mounted = true;
    setLoading(true);
    (async () => {
      try {
        const r = await api.get("/leads/response-time", { params: filters });
        if (mounted) setData(r.data);
      } finally { if (mounted) setLoading(false); }
    })();
    return () => { mounted = false; };
  }, [JSON.stringify(filters)]);

  if (loading && !data) {
    return (
      <div className="grid gap-4" style={{ gridTemplateColumns: "repeat(2, 1fr)" }}
        data-testid="response-time-card-loading">
        {[0,1].map(i => (
          <section key={i} className="surface p-6">
            <div className="animate-pulse bg-slate-100" style={{ height: 300, borderRadius: 8 }}/>
          </section>
        ))}
      </div>
    );
  }
  if (!data) return null;

  return (
    <div className="grid gap-4" style={{ gridTemplateColumns: "repeat(2, minmax(0, 1fr))" }}>
      <KpiPanel
        kpi={data.ftr}
        title="Primer Tiempo de Respuesta · BDC"
        subtitle="Leads digitales atendidos en el Contact Center"
        brandScope={(data.config?.ftr_brands || []).join(" · ")}
        tooltip={
          <>
            Tiempo desde la creación del lead hasta el <strong>primer contacto</strong> del equipo
            BDC. Solo aplica a Nissan / Chevrolet / Suzuki. SLA formal:
            {" "}<strong>≤ 2h en horario laboral</strong> (L–V 8am–6pm, Sáb 8am–1pm).
            El donut segmenta los leads atendidos en 4 zonas de acción: verde cumple SLA, amarillo
            mismo día, naranja siguiente día, rojo tardío.
          </>
        }
        accent="#0ea5e9"
        rankingLabel="Top BDC · por volumen atendido"
        testid="response-time-ftr"
      />
      <KpiPanel
        kpi={data.gestion}
        title="Gestión de Leads · Piso"
        subtitle="Leads en piso que pasan de Nuevo a un estado gestionado"
        brandScope={(data.config?.gestion_brands || []).join(" · ")}
        tooltip={
          <>
            Tiempo promedio desde <strong>Nuevo</strong> (Abierto) hasta que el ejecutivo de piso
            lo mueve a Contactado / Calificado / Descalificado. Excluye miembros del BDC.
            Cubre las 5 marcas (Nissan · Chevrolet · Suzuki · Cadillac · Infiniti).
          </>
        }
        accent="#8b5cf6"
        rankingLabel="Top Piso · por volumen atendido"
        testid="response-time-gestion"
      />
    </div>
  );
}
